import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { field, label } from "./ProductEditors";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const blank = { code: "", kind: "percent", value: "", max_uses: "", expires_at: "", note: "" };
const fmtValue = (c) => (c.kind === "percent" ? `${c.value}% off` : `$${Number(c.value).toFixed(2)} off`);

export const CouponsTab = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));

  useEffect(() => {
    axios.get(`${API}/admin/coupons`, { headers }).then((r) => setItems(r.data)).catch(() => toast.error("Could not load coupons."));
  }, [headers]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: f.code, kind: f.kind, value: parseFloat(f.value), note: f.note,
        max_uses: f.max_uses ? parseInt(f.max_uses, 10) : null,
        expires_at: f.expires_at ? new Date(f.expires_at).toISOString() : null,
      };
      const r = await axios.post(`${API}/admin/coupons`, payload, { headers });
      setItems((l) => [r.data, ...l]);
      setF(blank);
      toast.success(`Coupon ${r.data.code} is live`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create coupon.");
    } finally {
      setSaving(false);
    }
  };
  const toggle = async (c) => {
    try {
      const r = await axios.patch(`${API}/admin/coupons/${c.id}?active=${!c.active}`, null, { headers });
      setItems((l) => l.map((x) => (x.id === c.id ? r.data : x)));
    } catch {
      toast.error("Could not update coupon.");
    }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`, { headers });
      setItems((l) => l.filter((x) => x.id !== c.id));
      toast.success("Coupon deleted");
    } catch {
      toast.error("Could not delete coupon.");
    }
  };

  return (
    <div data-testid="admin-coupons" className="mt-8">
      <form onSubmit={create} data-testid="coupon-form" className="bg-[#faf9f6] border border-[#e3e2dc] rounded-2xl p-5 grid grid-cols-2 sm:grid-cols-6 gap-3 items-end">
        <div className="col-span-2"><span className={label}>Code</span>
          <input data-testid="coupon-code" className={`${field} uppercase`} required value={f.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} placeholder="FOUNDING100" /></div>
        <div><span className={label}>Type</span>
          <select data-testid="coupon-kind" className={field} value={f.kind} onChange={(e) => set({ kind: e.target.value })}>
            <option value="percent">% off</option><option value="fixed">$ off</option>
          </select></div>
        <div><span className={label}>{f.kind === "percent" ? "Percent" : "Dollars"}</span>
          <input data-testid="coupon-value" className={field} type="number" min="0.01" step="0.01" required value={f.value} onChange={(e) => set({ value: e.target.value })} placeholder={f.kind === "percent" ? "15" : "20"} /></div>
        <div><span className={label}>Max uses</span>
          <input data-testid="coupon-max" className={field} type="number" min="1" value={f.max_uses} onChange={(e) => set({ max_uses: e.target.value })} placeholder="∞" /></div>
        <div><span className={label}>Expires</span>
          <input data-testid="coupon-expires" className={field} type="date" value={f.expires_at} onChange={(e) => set({ expires_at: e.target.value })} /></div>
        <div className="col-span-2 sm:col-span-4"><span className={label}>Note (internal)</span>
          <input data-testid="coupon-note" className={field} value={f.note} onChange={(e) => set({ note: e.target.value })} placeholder="Founding 100 launch code" /></div>
        <button data-testid="coupon-create" type="submit" disabled={saving} className="col-span-2 h-10 rounded-full bg-[#ff2a85] hover:bg-[#e60067] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create coupon
        </button>
      </form>
      <p className="mt-3 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a]">Customers enter the code on the Stripe checkout page ("Add promotion code").</p>

      {items === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-[#5f5e5a]" /></div>
      ) : !items.length ? (
        <p data-testid="coupons-empty" className="mt-12 text-center text-[#5f5e5a] font-mono-d text-[11px] uppercase tracking-[0.2em]">No coupons yet.</p>
      ) : (
        <div className="mt-6 border border-[#e3e2dc] rounded-2xl overflow-hidden bg-[#faf9f6] divide-y divide-[#e3e2dc]">
          {items.map((c) => (
            <div key={c.id} data-testid={`coupon-row-${c.code}`} className={`flex items-center gap-4 px-5 py-3 ${c.active ? "" : "opacity-50"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-d font-semibold tracking-[0.15em]">{c.code}</span>
                  <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] bg-[#0a0a0b] text-white px-2 py-0.5 rounded-full">{fmtValue(c)}</span>
                  {!c.active && <span className="font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#ff2a85]">inactive</span>}
                </div>
                <p className="text-xs text-[#5f5e5a] mt-0.5">
                  {c.times_redeemed || 0} used{c.max_uses ? ` of ${c.max_uses}` : ""}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString("en-GB")}` : ""}{c.note ? ` · ${c.note}` : ""}
                </p>
              </div>
              <label className="flex items-center gap-2 font-mono-d text-[9px] uppercase tracking-[0.15em] text-[#5f5e5a] cursor-pointer">
                <input data-testid={`coupon-toggle-${c.code}`} type="checkbox" checked={c.active} onChange={() => toggle(c)} className="accent-[#ff2a85] h-4 w-4" /> Active
              </label>
              <button data-testid={`coupon-delete-${c.code}`} onClick={() => remove(c)} aria-label="Delete" className="text-[#5f5e5a] hover:text-[#ff2a85]"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
